import React, { Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import LayersIcon from "@mui/icons-material/Layers";
import SettingsIcon from "@mui/icons-material/Settings";
import StyleIcon from "@mui/icons-material/Style";
import MapIcon from "@mui/icons-material/Map";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import ConnectingAirportsIcon from "@mui/icons-material/ConnectingAirports";

import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Slide from "@mui/material/Slide";

import Feature from "ol/Feature";
import Polygon from "ol/geom/Polygon";
import Draw from "ol/interaction/Draw";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import Icon from "ol/style/Icon";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";

import LAANCDetailsDialog from "../manager/operations/laancDetailsDialog";

import { ConversationDialog } from "../chat/conversationDialog";
import { useUserAuth } from "../contexts/authContext";
import { useMap } from "../contexts/mapContext";
import { useEnv } from "../contexts/envContext";

import { ConstraintCircleCreateDialog } from "../manager/constraints/constraintCircleCreateDialog";
import { ConstraintDraw } from "../manager/constraints/constraintDraw";
import FlightCreator from "../manager/operations/flightCreator";
import { ConstraintEdit } from "../manager/constraints/constraintEdit";

import { MapFlightCards } from "../map/mapFlightCards";
import { MapSettingsDropdown } from "../map/mapSettingsDropdown";
import { MapLayersDropdown } from "./mapLayersDropdown";
import { EntityWatchToolbar } from "./entityWatchToolbar";

import MapTileDropdown from "./mapTileDropdown";
import MapInfoCard from "./mapInfoCard";
import MapFullScreen from "./mapFullScreen";
import MapTaggedVehicleCards from "./mapTaggedVehicleCards";
import WeatherLegendComponents from "../weatherLegendComponents";

import {
    getGeometryStyleFromFeatureAndSettings,
    getHistLineCoordinatesFromTrack,
    getPredLineCoordinatesFromTrackAndSettings,
    getZeroConflictionLineStyleFromZeroConflictMessage,
    ConvertCmpVolumesToFeatures,
    createZeroConflictionLineFeature,
    updateFeaturesZeroConflictionAlerts,
    GetFeature,
    GetFeatures,
    GetLayer,
    GetSource,
    JumpToFeatures,
    GetAllFeaturesWithId,
    updateZeroConflictLineFeature,
    GetLaancAirspaceStyle,
    getAsdFeatureStyleFromLayersAndSettings,
    getLabelTextFromFeatureAndUser,
    getDpzCoordinatesFromTrack,
    JumpToFeature,
    getUpdatedZeroConflictionAlertsFromZeroConflictMessage,
    getIsNewZeroConflictionAlert,
    getIsHigherSeverityZeroConflictionAlert,
    getTaggedCallsign,
    getHomeLocationFeature,
    getOuterDpzCoordinatesFromTrack,
    setNextLabelRotation,
    shouldHideAsd,
    getGeometryStyleFromColor,
    getCoordinateLocationFromSettings
} from "../map/mapUtil";

import { getGeometryFromHimsComponent } from "../hms/hmsUtil";

import useOpenLayersMap from "../hooks/useOpenLayersMap";
import useOpenLayersGeocoder from "../hooks/useGeocoder";
import useWeatherLayer from "../hooks/useWeatherLayer";
import useAirspaceLayer from "../hooks/useAirspaceLayer";
import useFullscreenElement from "../hooks/useFullscreenElement";
import useExternalFlightsLayer from "../hooks/useExternalFlightsLayer";

import alertConflictAudio from "../../public/static/files/alert_volume_conflict_audio.mp3";
import zcaCautionAudio from "../../public/static/files/zero_conflict_caution_audio.mp3";
import zcaWarningAudio from "../../public/static/files/zero_conflict_warning_audio.mp3";
import { useLocalStorage } from "../hooks/useLocalStorage";

const MapComponent = () => {
    const MAP_ID = "main-map";

    const { userMapSettings, userProfileSettings, updateProfileSettings, user, socket, snackbar, setSnackbar, updateMapSettings } = useUserAuth();
    const { publishedOperations, constraints, alertVolumes, coaVolumes, sensors, getDrones } = useMap();
    const { weatherOptions } = useEnv();

    const [createConstraintDialogOpen, setCreateConstraintDialogOpen] = useState(false);
    const [createConstraintVertices, setCreateConstraintVertices] = useState([]);

    const [firstResponderConstraintDialogOpen, setFirstResponderConstraintDialogOpen] = useState(false);
    const [firstResponderConstraintCenter, setFirstResponderConstraintCenter] = useState(false);
    const [drawingFirstResponderConstraint, setDrawingFirstResponderConstraint] = useState(false);

    const [flightDetailsVolume, setFlightDetailsVolume] = useState(false);
    const [flightDetailsDialogOpen, setFlightDetailsDialogOpen] = useState(false);

    const [mapSidebarOpen, setMapSidebarOpen] = useState(userProfileSettings.sidebar.open);
    const [mapSidebarPanelIndex, setMapSidebarPanelIndex] = useState(userProfileSettings.sidebar.index); // 0 for flight cards, 1 for tagged vehicles
    const [mapSidebarSlideDirection, setMapSidebarSlideDirection] = useState("right");

    const [asdWatchToolbarOpen, setAsdWatchToolbarOpen] = useState(userProfileSettings.toolbar.open);
    const [asdWatchToolbarData, setAsdWatchToolbarData] = useState(userProfileSettings.toolbar.data);
    const [audioAlertsEnabled, setAudioAlertsEnabled] = useState(userProfileSettings.toolbar.audio);
    const [followEntityEnabled, setFollowEntityEnabled] = useState(false);

    const [mapZoom, setMapZoom] = useState(null);
    const mapAnimating = useRef(false);

    const [taggedVehicles, setTaggedVehicles] = useState([]);
    const [activeZeroConflictionAlerts, setActiveZeroConflictionAlerts] = useState(new Map());
    const [activeAlertVolumeConflictionAlerts, setActiveAlertVolumeConflictionAlerts] = useState(new Map());
    const activeZeroConflictionAlertsRef = useRef(activeZeroConflictionAlerts);
    const activeAlertVolumeConflictionAlertsRef = useRef(activeAlertVolumeConflictionAlerts);

    const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
    const [editFlightVolume, setEditFlightVolume] = useState(false);
    const [editFlightOpen, setEditFlightOpen] = useState(false);

    const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
    const [mapSettingsAnchorEl, setMapSettingsAnchorEl] = useState(null);

    const [mapLayersOpen, setMapLayersOpen] = useState(false);
    const [mapLayersAnchorEl, setMapLayersAnchorEl] = useState(null);

    const [mapTileDropdownOpen, setMapTileDropdownOpen] = useState(false);
    const [mapTileDropdownAnchorEl, setMapTileDropdownAnchorEl] = useState(null);

    const [constraintEditDialogOpen, setConstraintEditDialogOpen] = useState(false);
    const [constraintEditEntity, setConstraintEditEntity] = useState(null);

    const [userMapLayers, setUserMapLayers] = useState(userProfileSettings.layers);
    const [labelRotations, setLabelRotations] = useLocalStorage("userLabelRotations", {});
    const featureToRotateRef = useRef(null);

    const mapWrapperRef = useRef(null);

    const [selectingNewHomeLocation, setSelectingNewHomeLocation] = useState(false);

    const mapLocationRef = useRef(null);
    const hoveredAsdFeatures = useRef([]);
    const volumeUuidToZoomTo = useRef("");
    const cursorCoordinateRef = useRef(null);

    const location = useLocation();
    const navigate = useNavigate();

    const map = useOpenLayersMap(MAP_ID, editFlightOpen);
    const mapContainer = useFullscreenElement();

    useOpenLayersGeocoder(map);
    useWeatherLayer(map, setUserMapLayers);
    useAirspaceLayer(map, setUserMapLayers);

    const [externalFlightsVisible, handleToggleExternalFlightsLayer] = useExternalFlightsLayer(map);

    useEffect(() => {
        getDrones();

        // Handle listening to map socket events only if they open up the map page.
        socket.emit("mapOpen");
        return () => socket.emit("mapClose");
    }, []);

    useEffect(() => {
        if (location.state && location.state.volumeUuidToZoomTo) {
            volumeUuidToZoomTo.current = location.state.volumeUuidToZoomTo;
        } else {
            volumeUuidToZoomTo.current = "";
        }
    }, [location]);

    useEffect(() => {
        if (!map) return;

        const layers = ["asd", "flights", "constraints", "alerts", "coa_volumes", "sensors"];
        layers.forEach((name) => {
            const vectorLayer = new VectorLayer({
                source: new VectorSource(),
                zIndex: 10,
                properties: {
                    name: name,
                    selectable: true
                },
                updateWhileAnimating: name === "asd" ? true : false,
                updateWhileInteracting: name === "asd" ? true : false
            });
            if (name === "asd") {
                vectorLayer.setZIndex(15);
            }
            map.addLayer(vectorLayer);
        });

        // Set initial map layer visibility.
        GetLayer(map, "asd")?.setVisible(userMapLayers.surveillance_sources);
        GetLayer(map, "flights")?.setVisible(userMapLayers.operations);
        GetLayer(map, "constraints")?.setVisible(userMapLayers.constraints);
        GetLayer(map, "alerts")?.setVisible(userMapLayers.alert_volumes);
        GetLayer(map, "coa_volumes")?.setVisible(userMapLayers.coa_volumes);
        GetLayer(map, "sensors")?.setVisible(userMapLayers.radar_footprint);

        // Set initial weather visibility
        weatherOptions.forEach((option) => {
            const layer = userMapLayers.weather_radar.find((weather) => weather.weather_name === option.name);
            GetLayer(map, option.name)?.setVisible(layer.visible);
        });
    }, [map]);

    useEffect(() => {
        if (!map) return;

        const interval = setInterval(() => {
            const dateNow = Date.now();
            const expirationTime = 10 * 1000;

            // ASD Expiration
            const source = GetSource(map, "asd");
            source?.getFeatures().forEach((feature) => {
                if (dateNow - feature.get("last_asd_update_ms") > expirationTime) {
                    source.removeFeature(feature);
                }
            });

            // ASD Label Rotation Expiration
            setLabelRotations((prev) => {
                const rotations = {};
                for (const id in prev) {
                    if (dateNow - prev[id].timestamp <= 120 * 1000) {
                        rotations[id] = prev[id];
                    }
                }
                return rotations;
            });

            // Zero Conflict Alert Expiration
            activeZeroConflictionAlertsRef.current.forEach((alerts) =>
                alerts.forEach((alert) => {
                    if (dateNow - new Date(alert.timestamp).getTime() > expirationTime) {
                        const expirationMessage = { ...alert, state: "EXPIRED" };
                        handleZeroConflictionMessage(expirationMessage);
                    }
                })
            );

            // Alert Volume Confliction Alert Expiration
            activeAlertVolumeConflictionAlertsRef.current.forEach((alert, uuid) => {
                if (dateNow - new Date(alert.time_of_alert).getTime() > expirationTime) {
                    GetFeature(map, "asd", uuid)?.unset("alert_volume_confliction_alert");
                    setAsdWatchToolbarData((prev) => (prev.track_id === uuid ? { ...prev, alert_conflict_alert: null } : prev));
                    activeAlertVolumeConflictionAlertsRef.current.delete(uuid);
                }
            });
            setActiveAlertVolumeConflictionAlerts(activeAlertVolumeConflictionAlertsRef.current);
        }, 1000);

        return () => clearInterval(interval);
    }, [map]);

    useEffect(() => {
        if (!map) return;

        let timeout;
        const handlePointerMove = ({ originalEvent, pixel }) => {
            const coordinates = map.getEventCoordinate(originalEvent);
            cursorCoordinateRef.current = coordinates;

            if (mapLocationRef.current) {
                mapLocationRef.current.innerHTML = getCoordinateLocationFromSettings(coordinates, userMapSettings);
            }

            // Ignore all non-hover events.
            if (userMapSettings.label_visible !== "off") {
                return;
            }

            hoveredAsdFeatures.current.forEach((feature) => {
                feature.set("show_label", false);
                feature.getStyle()?.forEach((style) => {
                    if (style.asd_track_label) {
                        style.setText(undefined);
                    }
                });
            });
            hoveredAsdFeatures.current = [];

            clearTimeout(timeout);
            timeout = setTimeout(() => {
                map.forEachFeatureAtPixel(pixel, (feature, layer) => {
                    if (!feature.get("asd") || layer !== GetLayer(map, "asd")) {
                        return;
                    }

                    const style = feature.getStyle();
                    if (Array.isArray(style) && style[0].getImage()) {
                        feature.set("show_label", true);
                        const labelStyle = new Style({ text: getLabelTextFromFeatureAndUser(feature, user, userMapSettings) });
                        labelStyle.asd_track_label = true;
                        feature.setStyle([...style, labelStyle]);
                        hoveredAsdFeatures.current.push(feature);
                    }
                });
            }, 250);
        };
        map.on("pointermove", handlePointerMove);

        // Pan to the follow entity if zoom value changed.
        const handleMoveEnd = () => setMapZoom(map.getView().getZoom());
        map.on("moveend", handleMoveEnd);

        // Moving stops the panning of the follow entity.
        const handlePointerDrag = () => setFollowEntityEnabled(false);
        map.on("pointerdrag", handlePointerDrag);

        // Rotate the data tag on right-click.
        const handleRotateRightClick = (e) => {
            e.preventDefault();

            const features = [];
            map.forEachFeatureAtPixel(map.getEventPixel(e), (feature, layer) => {
                if (feature.get("asd") && layer === GetLayer(map, "asd") && feature.getId().includes("_") === false) {
                    features.push(feature);
                }
            });

            if (features.length === 1) {
                setNextLabelRotation(features[0], setLabelRotations, userMapSettings);
                featureToRotateRef.current = features[0];
            }
        };
        map.getViewport().addEventListener("contextmenu", handleRotateRightClick);

        // Rotate the data tag when you press 'R'.
        const handleRotateHotKey = (e) => {
            if (featureToRotateRef.current && e.key.toLowerCase() === "r") {
                setNextLabelRotation(featureToRotateRef.current, setLabelRotations, userMapSettings);
            }
        };
        document.addEventListener("keydown", handleRotateHotKey);

        return () => {
            clearTimeout(timeout);
            map.un("pointermove", handlePointerMove);
            map.un("moveend", handleMoveEnd);
            map.un("pointerdrag", handlePointerDrag);
            map.getViewport().removeEventListener("contextmenu", handleRotateRightClick);
            document.removeEventListener("keydown", handleRotateHotKey);
        };
    }, [map, userMapSettings]);

    // update features when the map settings change
    useEffect(() => {
        GetLayer(map, "base")?.setOpacity(userMapSettings.brightness || 0);
        GetFeatures(map, "asd")?.forEach((f) => f.setStyle(getAsdFeatureStyleFromLayersAndSettings(f, userMapLayers, userMapSettings, user, taggedVehicles)));
        ["flights", "constraints", "alerts"].forEach((volume) => {
            GetFeatures(map, volume)?.forEach((f) => f.setStyle(getGeometryStyleFromFeatureAndSettings(f, userMapSettings)));
        });
        handleUpdateWatchEntity();

        // Set weather layer opacity
        weatherOptions.forEach((option) => {
            GetLayer(map, option.name)?.setOpacity(userMapSettings.weather_opacity || 0);
        });

        // update coordinate display in the lower right hand corner
        if (mapLocationRef.current) {
            mapLocationRef.current.innerHTML = getCoordinateLocationFromSettings(cursorCoordinateRef.current, userMapSettings);
        }
    }, [map, userMapSettings, userMapLayers]);

    // draw radar footprints
    useEffect(() => {
        const sensorSource = GetSource(map, "sensors");
        if (!sensorSource) {
            return;
        }

        sensorSource.clear();
        sensors.forEach((sensor) => {
            if (isNaN(sensor.lat) || isNaN(sensor.lon) || isNaN(sensor.radius)) {
                return;
            }

            const feature = new Feature({
                geometry: getGeometryFromHimsComponent(sensor),
                name: sensor.name,
                sensor,
                truth_source: sensor.truth_source
            });
            feature.setStyle(getGeometryStyleFromColor("#000000"));
            sensorSource.addFeature(feature);
        });
    }, [map, sensors]);

    useEffect(() => {
        const layers = [
            { name: "flights", volumes: publishedOperations },
            { name: "constraints", volumes: constraints },
            { name: "alerts", volumes: alertVolumes },
            { name: "coa_volumes", volumes: coaVolumes }
        ];
        layers.map(({ name, volumes }) => {
            const source = GetSource(map, name);
            if (!source) {
                return;
            }

            const features = ConvertCmpVolumesToFeatures(volumes, userMapSettings);
            source.clear();
            source.addFeatures(features);

            // remove labels for alert volumes that are associated with an operation
            features.filter((f) => f.get("alert_volume")?.flight_uuid).forEach((f) => f.getStyle().setText(undefined));

            if (name !== "constraints" || !volumeUuidToZoomTo.current) {
                return;
            }

            // constraint notification jumping
            const zoomFeature = features.find((f) => f.getId().includes(volumeUuidToZoomTo.current));
            if (zoomFeature) {
                JumpToFeature(map, zoomFeature);
            } else {
                setSnackbar({ children: "Constraint has been removed", severity: "error" });
            }
            volumeUuidToZoomTo.current = "";
            navigate("/map", { state: { volumeUuidToZoomTo: "" } });
        });
    }, [map, publishedOperations, constraints, alertVolumes, coaVolumes]);

    useEffect(() => {
        const asdSource = GetSource(map, "asd");
        if (!asdSource) {
            return;
        }

        const handleAsdMessage = (message) => {
            const lastUpdateTime = new Date(message.timestamp).getTime();
            const tracks = [
                { id: `${message.id}`, coordinates: [message.lon_deg, message.lat_deg] },
                { id: `${message.id}_pl`, coordinates: getPredLineCoordinatesFromTrackAndSettings(message, userMapSettings) },
                { id: `${message.id}_hl`, coordinates: getHistLineCoordinatesFromTrack(message, userMapSettings) },
                { id: `${message.id}_dpz`, coordinates: getDpzCoordinatesFromTrack(message, userMapSettings) },
                { id: `${message.id}_outer_dpz`, coordinates: getOuterDpzCoordinatesFromTrack(message, userMapSettings) }
            ];
            tracks.forEach(({ id, coordinates }, i) => {
                if (shouldHideAsd(message, user)) {
                    return;
                }

                const feature = asdSource.getFeatureById(id);
                const name = getTaggedCallsign(message, user);
                const labelRotation = labelRotations[id];

                if (feature) {
                    if (labelRotation) {
                        feature.set("label_rotation", labelRotation.rotation);
                        setLabelRotations((prev) => ({ ...prev, [id]: { rotation: labelRotation.rotation, timestamp: lastUpdateTime } }));
                    }

                    feature.getGeometry().setCoordinates(coordinates);
                    feature.setProperties({ asd: message, last_asd_update_ms: lastUpdateTime });
                    feature.setStyle(getAsdFeatureStyleFromLayersAndSettings(feature, userMapLayers, userMapSettings, user, taggedVehicles));

                    // update zero confliction lines if applicable
                    if (i === 0) {
                        updateZeroConflictLineFeature(map, feature);

                        const featureName = feature.get("name");
                        if (name !== featureName) {
                            feature.set("name", name);
                        }
                    }
                    return;
                }

                let geometry = new Point(coordinates);
                if (id.includes("_pl") || id.includes("_hl")) {
                    geometry = new LineString(coordinates);
                } else if (id.includes("_dpz")) {
                    geometry = new Polygon(coordinates);
                }

                const newFeature = new Feature({ geometry, name: name, asd: message, last_asd_update_ms: lastUpdateTime });
                if (labelRotation) {
                    newFeature.set("label_rotation", labelRotation.rotation);
                    setLabelRotations((prev) => ({ ...prev, [id]: { rotation: labelRotation.rotation, timestamp: lastUpdateTime } }));
                }
                newFeature.setId(id);
                newFeature.setStyle(getAsdFeatureStyleFromLayersAndSettings(newFeature, userMapLayers, userMapSettings, user, taggedVehicles));
                asdSource.addFeature(newFeature);
            });

            // update map layer display
            const source = userMapSettings.source_mapping[message.source_id];
            const sourceLabel = !source ? message.source_id : source.visibility ? source.identifier || message.source_id : null;
            if (sourceLabel && !userMapLayers.surveillance_sources_list.find(({ label }) => label === sourceLabel)) {
                handleMapLayersChange("surveillance_sources_list", { label: sourceLabel });
            }

            // update watch toolbar if applicable
            if (message.id === asdWatchToolbarData.track_id) {
                handleUpdateWatchEntity();
                setAsdWatchToolbarData((prev) => ({
                    ...prev,
                    tagged_callsign: getTaggedCallsign(message, user),
                    track_angle: message.course_deg,
                    ground_speed_kn: message.ground_speed_kn,
                    alt_agl_ft: message.alt_agl_ft,
                    alt_baro_ft: message.alt_baro_ft,
                    alt_corr_baro_ft: message.alt_corr_baro_ft,
                    alt_hae_ft: message.alt_hae_ft
                }));
            }
        };

        socket.on("asd", (messages) => messages.forEach(handleAsdMessage));
        socket.on("zero_conflict", handleZeroConflictionMessage);
        socket.on("airspace_alerting_conflicts", (message) => {
            if (alertVolumes.find(({ uuid }) => uuid === message.volume_id)) {
                handleAlertVolumeConflictionMessage(message);
            }
        });

        return () => {
            socket.off("asd");
            socket.off("zero_conflict");
            socket.off("airspace_alerting_conflicts");
        };
    }, [
        map,
        activeZeroConflictionAlerts,
        activeAlertVolumeConflictionAlerts,
        asdWatchToolbarData,
        userMapSettings,
        userMapLayers,
        user,
        alertVolumes,
        taggedVehicles,
        labelRotations
    ]);

    useEffect(() => {
        handleUpdateWatchEntity();

        if (followEntityEnabled && asdWatchToolbarData && asdWatchToolbarData.track_id !== "" && !mapAnimating.current) {
            handleFollowEntity(GetFeature(map, "asd", asdWatchToolbarData.track_id));
        }
    }, [asdWatchToolbarData, followEntityEnabled, mapZoom]);

    useEffect(() => {
        const updatedProfileSettings = {
            ...userProfileSettings,
            layers: userMapLayers,
            sidebar: {
                ...userProfileSettings.sidebar,
                open: mapSidebarOpen,
                index: mapSidebarPanelIndex
            },
            toolbar: {
                open: asdWatchToolbarOpen,
                audio: audioAlertsEnabled,
                data: asdWatchToolbarData
            }
        };
        updateProfileSettings(updatedProfileSettings);
    }, [userMapLayers, mapSidebarOpen, mapSidebarPanelIndex, asdWatchToolbarOpen, asdWatchToolbarData, audioAlertsEnabled]);

    useEffect(() => {
        if (!socket) return;

        socket.on("update_tagged_vehicles", (vehicles) => setTaggedVehicles(vehicles));
        socket.emit("get_tagged_vehicles", user.id);

        return () => {
            if (!socket) return;
            socket.off("update_tagged_vehicles");
        };
    }, [socket]);

    const handleZeroConflictionMessage = (message) => {
        if (!map) return;

        const ownshipID = message.ownship_track.id;
        const intruderID = message.intruder_track.id;
        const zcLineID = `${ownshipID}_${intruderID}`;

        const ownshipFeatures = GetFeature(map, "asd", ownshipID);
        const intruderFeatures = GetFeature(map, "asd", intruderID);
        const zcLineFeatures = GetFeature(map, "asd", zcLineID);

        const taggedVehicle = taggedVehicles.find((v) => v.entity_uuid === ownshipID);
        if (user.user_role !== "Admin" && (!taggedVehicle || taggedVehicle.organization_id !== user.organization_id)) {
            return;
        }

        // update ownship and intruder feature styles
        [ownshipFeatures, intruderFeatures].forEach((feature) => {
            if (feature) {
                updateFeaturesZeroConflictionAlerts(feature, message);
                feature.setStyle(getAsdFeatureStyleFromLayersAndSettings(feature, userMapLayers, userMapSettings, user));
            }
        });

        handleUpdateWatchEntity();

        // create or update the line
        const asdSource = GetSource(map, "asd");
        if (ownshipFeatures && intruderFeatures && asdSource) {
            if (message.state === "ACTIVE") {
                if (zcLineFeatures) {
                    zcLineFeatures.setStyle(getZeroConflictionLineStyleFromZeroConflictMessage(message, ownshipFeatures));
                } else {
                    asdSource.addFeature(createZeroConflictionLineFeature(ownshipFeatures, intruderFeatures, message));
                }
            } else if (message.state === "EXPIRED" && zcLineFeatures) {
                asdSource.removeFeature(zcLineFeatures);
            }
        }
        // remove line if ownship or intruder no longer exists
        if (asdSource && zcLineFeatures && (!ownshipFeatures || !intruderFeatures)) {
            asdSource.removeFeature(zcLineFeatures);
        }
        // update state
        setActiveZeroConflictionAlerts((prev) => {
            const isNewAlert = getIsNewZeroConflictionAlert(prev, message);
            const isHigherSeverityAlert = getIsHigherSeverityZeroConflictionAlert(prev, message);

            if (isNewAlert || isHigherSeverityAlert) {
                if (user.user_role === "Airspace Manager") {
                    handleOpenFlightCards();
                }
                if (asdWatchToolbarData.track_id === ownshipID || asdWatchToolbarData.track_id === intruderID) {
                    playZeroConflictionSound(message);
                }
            }
            const zcAlerts = getUpdatedZeroConflictionAlertsFromZeroConflictMessage(prev, message);
            activeZeroConflictionAlertsRef.current = zcAlerts;

            if (asdWatchToolbarData.track_id === ownshipID || asdWatchToolbarData.track_id === intruderID) {
                setAsdWatchToolbarData((prev) => ({ ...prev, zero_conflict_alerts: zcAlerts.get(ownshipID) || [] }));
            }
            return zcAlerts;
        });
    };

    const handleAlertVolumeConflictionMessage = (message) => {
        if (!map) return;

        const vehicleID = message.track_id;
        const vehicleFeature = GetFeature(map, "asd", vehicleID);

        // set feature property to indicate alert, update style if needed
        if (vehicleFeature) {
            vehicleFeature.set("alert_volume_confliction_alert", message);
            vehicleFeature.setStyle(getAsdFeatureStyleFromLayersAndSettings(vehicleFeature, userMapLayers, userMapSettings, user, taggedVehicles));
        }

        // set watch toolbar data, send sound alert if new
        if (asdWatchToolbarData.track_id === vehicleID) {
            if (!activeAlertVolumeConflictionAlertsRef.current.has(vehicleID)) {
                playAlertVolumeConflictionSound();
            }
            setAsdWatchToolbarData({ ...asdWatchToolbarData, alert_conflict_alert: message });
        }

        activeAlertVolumeConflictionAlertsRef.current.set(vehicleID, message);
        setActiveAlertVolumeConflictionAlerts(activeAlertVolumeConflictionAlertsRef.current);
    };

    const handleHomeButtonClick = () => {
        if (map && map.getView()) {
            map.getView().setCenter([userMapSettings.longitude, userMapSettings.latitude]);
            map.getView().setZoom(12);
        }
    };

    const handleCreateConstraintButtonClick = () => {
        const draw = new Draw({
            source: new VectorSource({ wrapX: false }),
            type: "Polygon"
        });
        draw.on("drawend", ({ feature }) => {
            const coordinates = feature.getGeometry().getCoordinates();
            const vertices = coordinates[0].map((arr) => ({ lng: arr[0], lat: arr[1] }));
            setCreateConstraintVertices([{ id: 0, vertices }]);
            setCreateConstraintDialogOpen(true);
            setTimeout(() => map.removeInteraction(draw), 250);
        });
        map.addInteraction(draw);
    };

    const handleFirstResponderConstraintButtonClick = () => {
        const draw = new Draw({
            source: new VectorSource({ wrapX: false }),
            type: "Point"
        });
        draw.on("drawend", ({ feature }) => {
            const coordinates = feature.getGeometry().getCoordinates();
            const center = { lon_deg: coordinates[0], lat_deg: coordinates[1] };
            setFirstResponderConstraintCenter(center);
            setFirstResponderConstraintDialogOpen(true);
            setDrawingFirstResponderConstraint(false);
            setTimeout(() => map.removeInteraction(draw), 250);
        });
        setDrawingFirstResponderConstraint(true);
        map.addInteraction(draw);
    };

    const handleToggleLayersButtonClick = (e) => {
        setMapLayersAnchorEl(e.currentTarget);
        setMapLayersOpen((prev) => !prev);
    };

    const handleSettingsButtonClick = (e) => {
        setMapSettingsOpen((prev) => !prev);
        setMapSettingsAnchorEl(e.currentTarget);
    };

    const handleWatchEntityButtonClick = (asdUUID) => {
        const feature = GetFeature(map, "asd", asdUUID);
        const opened = feature && feature.get("asd") && asdWatchToolbarData.track_id !== asdUUID;
        setAsdWatchToolbarOpen(opened);
        setFollowEntityEnabled(opened);

        // Reset prev watch entity style.
        const prevFeature = GetFeature(map, "asd", asdWatchToolbarData.track_id);
        if (prevFeature) {
            prevFeature.setStyle(getAsdFeatureStyleFromLayersAndSettings(prevFeature, userMapLayers, userMapSettings, user, taggedVehicles));
        }

        if (!opened) {
            setAsdWatchToolbarData({
                track_id: "",
                tagged_callsign: "",
                track_angle: "",
                ground_speed_kn: "",
                alt_agl_ft: "",
                alt_baro_ft: "",
                alt_corr_baro_ft: "",
                alt_hae_ft: "",
                zero_conflict_alerts: [],
                alert_conflict_alert: null
            });
        } else {
            const asd = feature.get("asd");
            setAsdWatchToolbarData({
                track_id: asd.id,
                tagged_callsign: getTaggedCallsign(asd, user),
                track_angle: asd.course_deg,
                ground_speed_kn: asd.ground_speed_kn,
                alt_agl_ft: asd.alt_agl_ft,
                alt_baro_ft: asd.alt_baro_ft,
                alt_corr_baro_ft: asd.alt_corr_baro_ft,
                alt_hae_ft: asd.alt_hae_ft,
                zero_conflict_alerts: activeZeroConflictionAlerts.get(asdUUID) || [],
                alert_conflict_alert: activeAlertVolumeConflictionAlerts.get(asdUUID) || null
            });

            // Immediately animate to the entity.
            handleFollowEntity(feature);
        }
    };

    const handleUpdateWatchEntity = () => {
        if (!map || !asdWatchToolbarData || asdWatchToolbarData.track_id === "") {
            return;
        }

        const feature = GetFeature(map, "asd", asdWatchToolbarData.track_id);
        if (feature && Array.isArray(feature.getStyle()) && feature.getStyle().length > 0 && feature.getStyle()[0].getImage()) {
            const styles = feature.getStyle();
            const iconStyle = styles[0].getImage();
            const newStyle = new Style({
                image: new Icon({
                    src: iconStyle.getSrc(),
                    color: iconStyle.getColor(),
                    scale: userMapSettings.entity_size + 0.3,
                    rotation: iconStyle.getRotation()
                })
            });
            feature.setStyle([newStyle, ...styles.slice(1)]);
        }
    };

    const handleFollowEntity = (feature) => {
        if (!feature) {
            return;
        } else if (!Array.isArray(feature.getStyle()) || feature.getStyle().length === 0) {
            setFollowEntityEnabled(false);
            return;
        }

        const coords = feature?.getGeometry()?.getCoordinates();
        if (coords) {
            mapAnimating.current = true;
            map.getView().animate(
                {
                    center: coords,
                    duration: mapZoom > 12 ? 500 : 1000
                },
                () => (mapAnimating.current = false)
            );
        }
    };

    const handleCheckFlightASDVisible = (asdUUID) => {
        return !!GetFeature(map, "asd", asdUUID);
    };

    const handleConstraintEditButtonClick = (feature) => {
        setConstraintEditEntity({ constraint: feature.get("constraint") });
        setConstraintEditDialogOpen(true);
    };

    const handleJumpToOperationButtonClick = (flightUUID) => {
        JumpToFeatures(map, GetAllFeaturesWithId(map, "flights", flightUUID));
    };

    const handleMapLayersChange = (setting, value) => {
        const mapLayers = { ...userMapLayers };
        const toggleSettings = ["surveillance_sources", "operations", "constraints", "alert_volumes", "coa_volumes", "radar_footprint"];

        if (toggleSettings.includes(setting)) {
            const visibility = !mapLayers[setting];
            GetLayer(map, value)?.setVisible(visibility);
            mapLayers[setting] = visibility;
        } else if (setting === "surveillance_sources_list") {
            mapLayers[setting].push(value);
        } else if (setting === "airspaces") {
            const airspaces = mapLayers[setting];
            if (value) {
                const airspace = airspaces.find((airspace) => airspace.local_type === value);
                airspace.visible = !airspace.visible;
            } else {
                const visibility = !airspaces[0].visible;
                airspaces.forEach((airspace) => (airspace.visible = visibility));
            }
            GetLayer(map, "LAANC_AIRSPACE")?.setStyle((feature) => GetLaancAirspaceStyle(feature, airspaces));
        } else if (setting === "weather_radar") {
            const weatherRadar = mapLayers[setting];
            if (value) {
                const weather = weatherRadar.find((option) => option.weather_name === value);
                weather.visible = !weather.visible;
                GetLayer(map, value)?.setVisible(weather.visible);
            } else {
                const visibility = !weatherRadar[0].visible;
                weatherRadar.forEach((option) => {
                    if (option.disabled === true) return;
                    option.visible = visibility;
                    GetLayer(map, option.weather_name)?.setVisible(visibility);
                });
            }
        } else {
            mapLayers[setting] = value;
        }

        setUserMapLayers(mapLayers);
    };

    const handleShowEntityDpz = (message) => {
        const feature = GetFeature(map, "asd", message.id);
        if (feature && feature.get("asd")) {
            const asd = feature.get("asd");
            const updatedAsd = {
                ...asd,
                flight_uuid: message.flight_uuid,
                tagged_callsign: {
                    ...asd.tagged_callsign,
                    [user.organization_id]: message.tagged_callsign
                },
                show_dpz: {
                    ...asd.show_dpz,
                    [user.organization_id]: message.show_dpz
                },
                daa_configs: {
                    ...asd.daa_configs,
                    [user.organization_id]: message.alerting_params_uuid
                }
            };
            feature.set("asd", updatedAsd);

            const updatedAsdStyle = getAsdFeatureStyleFromLayersAndSettings(feature, userMapLayers, userMapSettings, user, taggedVehicles);
            feature.setStyle(updatedAsdStyle);

            const updatedName = getTaggedCallsign(updatedAsd, user);
            feature.set("name", updatedName);
            handleUpdateWatchEntity();
        }
    };

    const playZeroConflictionSound = (zcMessage) => {
        if (!audioAlertsEnabled) {
            return;
        } else if (zcMessage.severity === "CAUTION") {
            new Audio(zcaCautionAudio).play();
        } else if (zcMessage.severity === "WARNING") {
            new Audio(zcaWarningAudio).play();
        }
    };

    const playAlertVolumeConflictionSound = () => {
        if (audioAlertsEnabled) {
            new Audio(alertConflictAudio).play();
        }
    };

    const handleMapTileDropdownButtonClick = (e) => {
        setMapTileDropdownOpen((prev) => !prev);
        setMapTileDropdownAnchorEl(e.currentTarget);
    };

    const handleFullScreenButtonClick = () => {
        try {
            if (mapContainer) {
                document.exitFullscreen();
            } else {
                mapWrapperRef.current.requestFullscreen();
            }
        } catch (err) {
            console.error(err);
        }
    };
    const handleFlightCardsButtonClick = () => {
        if (mapSidebarOpen === false) {
            setMapSidebarOpen(true);

            if (mapSidebarPanelIndex !== 0) {
                setMapSidebarPanelIndex(0);
            }
        } else if (mapSidebarPanelIndex === 0) {
            setMapSidebarOpen(false);
        } else {
            setMapSidebarPanelIndex(0);
        }
    };
    const handleTaggedVehiclesButtonClick = () => {
        if (mapSidebarOpen === false) {
            setMapSidebarOpen(true);

            if (mapSidebarPanelIndex !== 1) {
                setMapSidebarPanelIndex(1);
            }
        } else if (mapSidebarPanelIndex === 1) {
            setMapSidebarOpen(false);
        } else {
            setMapSidebarPanelIndex(1);
        }
    };
    const handleChangeMapSidebarPanel = (panelIdx) => {
        if (panelIdx < mapSidebarPanelIndex && mapSidebarSlideDirection !== "right") {
            setMapSidebarSlideDirection("right");
        } else if (panelIdx > mapSidebarPanelIndex && mapSidebarSlideDirection !== "left") {
            setMapSidebarSlideDirection("left");
        }
        setMapSidebarPanelIndex(panelIdx);
    };
    const handleNarTrackSettingChange = (e) => {
        const mapLayers = {
            ...userMapLayers,
            nar_tracks: e.target.checked
        };
        setUserMapLayers(mapLayers);
    };
    const handleSelectNewHomeLocationButton = () => {
        if (!map) return;
        setMapSettingsOpen(false);
        setSelectingNewHomeLocation(true);

        const homeLocationFeature = getHomeLocationFeature(userMapSettings);

        const vectorSource = new VectorSource({ features: [homeLocationFeature] });
        const vectorLayer = new VectorLayer({ source: vectorSource });
        map.addLayer(vectorLayer);

        const handlePointerMove = ({ coordinate }) => {
            homeLocationFeature.getGeometry().setCoordinates(coordinate);
        };
        map.on("pointermove", handlePointerMove);

        const handleMapClick = ({ coordinate }) => {
            setSelectingNewHomeLocation(false);
            map.removeLayer(vectorLayer);

            map.un("click", handleMapClick);
            map.un("pointermove", handlePointerMove);

            const confirmed = confirm("Are you sure you would like to set this as your new home location?");
            if (confirmed === false) return;

            const updatedMapSettings = {
                ...userMapSettings,
                latitude: coordinate[1].toFixed(4),
                longitude: coordinate[0].toFixed(4)
            };
            updateMapSettings(updatedMapSettings);
        };
        map.on("click", handleMapClick);
    };
    const handleOpenFlightCards = () => {
        if (mapSidebarOpen === false) {
            setMapSidebarOpen(true);
        }
        if (mapSidebarPanelIndex !== 1) {
            setMapSidebarPanelIndex(1);
        }
    };
    return editFlightOpen ? (
        <FlightCreator
            setFlightCreatorOpen={setEditFlightOpen}
            flightUUID={editFlightVolume.flight_uuid}
            publishedFlight={true}
            flightState={editFlightVolume.state}
        />
    ) : (
        <Box ref={mapWrapperRef} sx={{ width: "100%", position: "relative", overflowX: "hidden" }}>
            <Box sx={{ display: "flex", flex: 1, height: "100%" }}>
                <Box sx={{ flex: 1, position: "relative", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <EntityWatchToolbar
                        asdWatchToolbarOpen={asdWatchToolbarOpen}
                        asdWatchToolbarData={asdWatchToolbarData}
                        handleWatchEntityButtonClick={handleWatchEntityButtonClick}
                        audioAlertsEnabled={audioAlertsEnabled}
                        setAudioAlertsEnabled={setAudioAlertsEnabled}
                        followEntityEnabled={followEntityEnabled}
                        setFollowEntityEnabled={setFollowEntityEnabled}
                    />
                    <Box sx={{ flex: 1, position: "relative" }}>
                        <MapFullScreen
                            map_id={MAP_ID}
                            map={map}
                            buttons={[
                                {
                                    id: "viewHome",
                                    name: "View Home",
                                    visible: true,
                                    icon: <HomeIcon />,
                                    onClick: handleHomeButtonClick
                                },
                                {
                                    id: "createConstraint",
                                    name: "Create Constraint",
                                    visible: user.user_role === "Admin" || user.user_role === "Airspace Manager",
                                    icon: <EditIcon />,
                                    onClick: handleCreateConstraintButtonClick
                                },
                                {
                                    id: "toggleLayers",
                                    name: "Toggle Layers",
                                    visible: true,
                                    icon: <LayersIcon />,
                                    onClick: handleToggleLayersButtonClick
                                },
                                {
                                    id: "flightCards",
                                    name: "Flight Cards",
                                    visible: user.user_role !== "First Responder",
                                    icon: <StyleIcon />,
                                    onClick: handleFlightCardsButtonClick
                                },
                                {
                                    id: "taggedVehicles",
                                    name: "Tagged Vehicles",
                                    visible: user.user_role !== "First Responder",
                                    icon: <ConnectingAirportsIcon />,
                                    onClick: handleTaggedVehiclesButtonClick
                                },
                                {
                                    id: "editSettings",
                                    name: "Edit Settings",
                                    visible: true,
                                    icon: <SettingsIcon />,
                                    onClick: handleSettingsButtonClick
                                },
                                {
                                    id: "toggleMapTiles",
                                    name: "Map Tiles",
                                    visible: true,
                                    icon: <MapIcon />,
                                    onClick: handleMapTileDropdownButtonClick
                                },
                                {
                                    id: "toggleFullscreen",
                                    name: mapContainer ? "Exit Fullscreen" : "Enter Fullscreen",
                                    visible: true,
                                    icon: mapContainer ? <CloseIcon /> : <OpenInFullIcon />,
                                    onClick: handleFullScreenButtonClick
                                }
                            ]}
                        >
                            <MapInfoCard
                                map={map}
                                showButtons={true}
                                handleWatchEntityButtonClick={handleWatchEntityButtonClick}
                                handleEditConstraintButtonClick={handleConstraintEditButtonClick}
                                airspaceSubLayers={userMapLayers.airspaces}
                                mapContainer={mapContainer}
                                handleShowEntityDpz={handleShowEntityDpz}
                                featureToRotateRef={featureToRotateRef}
                            />
                        </MapFullScreen>
                        <Typography
                            ref={mapLocationRef}
                            variant="caption"
                            sx={{
                                position: "absolute",
                                right: "0.5em",
                                bottom: ".5em",
                                zIndex: 1,
                                fontFamily: "Arial,sans-serif",
                                textShadow:
                                    "-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black, -1px 0 0 black, 1px 0 0 black, 0 1px 0 black, 0 -1px 0 black, 0 0 5px black"
                            }}
                        />
                    </Box>

                    <WeatherLegendComponents />
                </Box>

                {/* map sidebar */}
                <Collapse in={mapSidebarOpen} orientation="horizontal" unmountOnExit sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
                    <Box sx={{ display: "flex", flexDirection: "row", width: { xs: "100vw", sm: "400px" } }}>
                        {[
                            {
                                element: (
                                    <MapFlightCards
                                        operations={publishedOperations}
                                        flightDetailsDialogOpen={flightDetailsDialogOpen}
                                        setFlightDetailsVolume={setFlightDetailsVolume}
                                        setFlightDetailsDialogOpen={setFlightDetailsDialogOpen}
                                        setConversationDialogOpen={setConversationDialogOpen}
                                        setEditFlightVolume={setEditFlightVolume}
                                        setEditFlightDialogOpen={setEditFlightOpen}
                                        handleWatchEntity={handleWatchEntityButtonClick}
                                        handleCheckFlightASDVisible={handleCheckFlightASDVisible}
                                        handleJumpToOperation={handleJumpToOperationButtonClick}
                                        activeAlertVolumeConflictionAlerts={activeAlertVolumeConflictionAlerts}
                                        activeZeroConflictionAlerts={activeZeroConflictionAlerts}
                                        mapSidebarOpen={mapSidebarOpen}
                                        setMapSidebarOpen={setMapSidebarOpen}
                                        mapSidebarPanelIndex={mapSidebarPanelIndex}
                                        handleChangeMapSidebarPanel={handleChangeMapSidebarPanel}
                                        mapContainer={mapContainer}
                                    />
                                )
                            },
                            {
                                element: (
                                    <MapTaggedVehicleCards
                                        map={map}
                                        mapSidebarOpen={mapSidebarOpen}
                                        setMapSidebarOpen={setMapSidebarOpen}
                                        mapSidebarPanelIndex={mapSidebarPanelIndex}
                                        handleChangeMapSidebarPanel={handleChangeMapSidebarPanel}
                                        handleWatchEntityButtonClick={handleWatchEntityButtonClick}
                                        activeAlertVolumeConflictionAlerts={activeAlertVolumeConflictionAlerts}
                                        activeZeroConflictionAlerts={activeZeroConflictionAlerts}
                                        mapContainer={mapContainer}
                                    />
                                )
                            }
                        ].map(({ element }, index) => (
                            <Box key={index} sx={{ width: "100%", height: "100%", display: mapSidebarPanelIndex === index ? "block" : "none" }}>
                                <Slide direction={mapSidebarSlideDirection} in={mapSidebarPanelIndex === index} appear={false}>
                                    <Box>{element}</Box>
                                </Slide>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            </Box>
            {constraintEditDialogOpen && (
                <ConstraintEdit
                    snackbar={snackbar}
                    constraintEditEntity={constraintEditEntity}
                    constraintEditDialogOpen={constraintEditDialogOpen}
                    setSnackbar={setSnackbar}
                    setConstraintEditDialogOpen={setConstraintEditDialogOpen}
                    mapContainer={mapContainer}
                />
            )}
            {createConstraintDialogOpen && (
                <ConstraintDraw
                    snackbar={snackbar}
                    constraintCreateVertices={createConstraintVertices}
                    constraintCreateDialogOpen={createConstraintDialogOpen}
                    setSnackbar={setSnackbar}
                    setConstraintCreateDialogOpen={setCreateConstraintDialogOpen}
                    mapContainer={mapContainer}
                />
            )}
            {firstResponderConstraintDialogOpen && (
                <ConstraintCircleCreateDialog
                    snackbar={snackbar}
                    constraintCircleCenterPoint={firstResponderConstraintCenter}
                    constraintCircleCreateDialogOpen={firstResponderConstraintDialogOpen}
                    setConstraintCircleCreateDialogOpen={setFirstResponderConstraintDialogOpen}
                    setSnackbar={setSnackbar}
                    mapContainer={mapContainer}
                />
            )}
            {user.user_role === "First Responder" && (
                <Fab
                    color="primary"
                    onClick={handleFirstResponderConstraintButtonClick}
                    disabled={drawingFirstResponderConstraint}
                    sx={{ position: "absolute", bottom: "40px", right: "10px", zIndex: "unset" }}
                    id="firstResponderConstraint"
                >
                    <AddIcon />
                </Fab>
            )}
            {flightDetailsDialogOpen && (
                <Suspense fallback={<></>}>
                    <LAANCDetailsDialog
                        flightDetailsVolume={flightDetailsVolume}
                        flightDetailsDialogOpen={flightDetailsDialogOpen}
                        publishedOperations={publishedOperations}
                        setFlightDetailsVolume={setFlightDetailsVolume}
                        setFlightDetailsDialogOpen={setFlightDetailsDialogOpen}
                        mapContainer={mapContainer}
                    />
                </Suspense>
            )}
            {conversationDialogOpen && (
                <ConversationDialog
                    conversationDialogOpen={conversationDialogOpen}
                    setConversationDialogOpen={setConversationDialogOpen}
                    mapContainer={mapContainer}
                />
            )}
            {mapSettingsOpen && (
                <MapSettingsDropdown
                    mapSettingsOpen={mapSettingsOpen}
                    setMapSettingsOpen={setMapSettingsOpen}
                    mapSettingsAnchorEl={mapSettingsAnchorEl}
                    setSnackbar={setSnackbar}
                    mapContainer={mapContainer}
                    handleSelectNewHomeLocationButton={handleSelectNewHomeLocationButton}
                    selectingNewHomeLocation={selectingNewHomeLocation}
                />
            )}
            <MapLayersDropdown
                mapLayersOpen={mapLayersOpen}
                setMapLayersOpen={setMapLayersOpen}
                mapLayersAnchorEl={mapLayersAnchorEl}
                altitudeLayerRange={userMapLayers.altitude_range}
                handleAltitudeLayerRangeChanged={(e) => handleMapLayersChange("altitude_range", e.target.value)}
                narChecked={userMapLayers.nar_tracks}
                handleNarTrackSettingChange={handleNarTrackSettingChange}
                sections={[
                    {
                        title: "General",
                        layers: [
                            {
                                label: "Surveillance Sources",
                                checked: userMapLayers.surveillance_sources,
                                handleLayerClick: () => handleMapLayersChange("surveillance_sources", "asd"),
                                sublayers: userMapLayers.surveillance_sources_list
                            },
                            {
                                label: "Weather Radar",
                                checked: userMapLayers.weather_radar.every(({ visible }) => visible),
                                indeterminate: !userMapLayers.weather_radar.every(({ visible }) => visible === userMapLayers.weather_radar[0].visible),
                                handleLayerClick: () => handleMapLayersChange("weather_radar"),
                                sublayers: userMapLayers.weather_radar.map((weather) => {
                                    return {
                                        label: weather.weather_name,
                                        checked: weather.visible,
                                        handleSublayerClick: () => handleMapLayersChange("weather_radar", weather.weather_name),
                                        disabled: weather.disabled
                                    };
                                }),
                                visibility: Array.isArray(userMapLayers.weather_radar) && userMapLayers.weather_radar.length > 0
                            }
                        ]
                    },
                    {
                        title: "Volumes",
                        layers: [
                            {
                                label: "Airspaces",
                                checked: userMapLayers.airspaces.every(({ visible }) => visible),
                                indeterminate: !userMapLayers.airspaces.every(({ visible }) => visible === userMapLayers.airspaces[0].visible),
                                handleLayerClick: () => handleMapLayersChange("airspaces"),
                                sublayers: userMapLayers.airspaces.map((airspace) => {
                                    return {
                                        label: airspace.local_type,
                                        checked: airspace.visible,
                                        handleSublayerClick: () => handleMapLayersChange("airspaces", airspace.local_type),
                                        disabled: airspace.disabled
                                    };
                                })
                            },
                            {
                                label: "Operations",
                                checked: userMapLayers.operations,
                                handleLayerClick: () => handleMapLayersChange("operations", "flights"),
                                sublayers: []
                            },
                            {
                                label: "Constraints",
                                checked: userMapLayers.constraints,
                                handleLayerClick: () => handleMapLayersChange("constraints", "constraints"),
                                sublayers: []
                            },
                            {
                                label: "Alert Volumes",
                                checked: userMapLayers.alert_volumes,
                                handleLayerClick: () => handleMapLayersChange("alert_volumes", "alerts"),
                                sublayers: []
                            },
                            {
                                label: "COA Volumes",
                                checked: userMapLayers.coa_volumes,
                                handleLayerClick: () => handleMapLayersChange("coa_volumes", "coa_volumes"),
                                sublayers: []
                            },
                            {
                                label: "Radar Footprint",
                                checked: userMapLayers.radar_footprint,
                                handleLayerClick: () => handleMapLayersChange("radar_footprint", "sensors"),
                                sublayers: []
                            },
                            {
                                label: "External Flights",
                                checked: externalFlightsVisible,
                                handleLayerClick: () => handleToggleExternalFlightsLayer(),
                                sublayers: []
                            }
                        ]
                    }
                ]}
                mapContainer={mapContainer}
            />
            <MapTileDropdown
                mapTileDropdownOpen={mapTileDropdownOpen}
                setMapTileDropdownOpen={setMapTileDropdownOpen}
                mapTileDropdownAnchorEl={mapTileDropdownAnchorEl}
                mapContainer={mapContainer}
            />
        </Box>
    );
};

export default MapComponent;
